import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { approveMessage, cancelMessage } from '../services/apiTutor';
import { getConversations, getConversationMessages, updateContact } from '../services/apiContacts';
import AiResponseModal from '../components/chat/AiResponseModal';
import echo from '../lib/echo';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

export default function TutorPage() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [selectedConvId, setSelectedConvId] = useState(null);
    const [editText, setEditText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [tabFilter, setTabFilter] = useState('all'); // 'all' | 'pending'
    const [showMenu, setShowMenu] = useState(false);
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const messagesEndRef = useRef(null);

    // Fetch all conversations
    const { data: rawConversations = [], isLoading: loadingConvs } = useQuery({
        queryKey: ['tutor-conversations'],
        queryFn: () => getConversations(),
    });

    const conversations = Array.isArray(rawConversations) ? rawConversations : (rawConversations.data || []);

    // Filter conversations based on tab and search query
    const filteredConversations = conversations.filter(conv => {
        const contact = conv.contact || {};
        const contactName = `${contact.first_name || ''} ${contact.last_name || ''} ${contact.name || ''}`.toLowerCase();
        const phone = (contact.phone || '').toLowerCase();
        const lastMsg = (conv.latest_message?.body || '').toLowerCase();
        const query = searchQuery.toLowerCase();

        const matchesSearch = !query || contactName.includes(query) || phone.includes(query) || lastMsg.includes(query);

        const convMsgs = Array.isArray(conv.messages) ? conv.messages : (conv.messages?.data || []);
        const hasPending = conv.has_pending_tutor || conv.latest_message?.is_pending_tutor_approval || convMsgs.some(m => m?.is_pending_tutor_approval);

        if (tabFilter === 'pending') {
            return matchesSearch && hasPending;
        }

        return matchesSearch;
    });

    const activeConversation = conversations.find(c => c.id === selectedConvId) || (filteredConversations.find(c => c.id === selectedConvId));

    // Fetch messages for active conversation
    const { data: rawMessages = [], isLoading: loadingMessages } = useQuery({
        queryKey: ['tutor-messages', selectedConvId],
        queryFn: () => getConversationMessages(selectedConvId),
        enabled: !!selectedConvId,
    });

    const messages = Array.isArray(rawMessages) ? rawMessages : (rawMessages?.data || []);

    // Find the pending AI message in current conversation
    const pendingAiMessage = messages.find(m => m.is_pending_tutor_approval);

    // Sync input field with pending message
    useEffect(() => {
        if (pendingAiMessage) {
            setEditText(pendingAiMessage.body || '');
        } else {
            setEditText('');
        }
    }, [pendingAiMessage, selectedConvId]);

    // Auto-select first conversation if list loaded and none selected
    useEffect(() => {
        if (filteredConversations.length > 0 && !selectedConvId) {
            setSelectedConvId(filteredConversations[0].id);
        }
    }, [filteredConversations, selectedConvId]);

    // WebSocket subscription for real-time updates
    useEffect(() => {
        if (!selectedConvId) return;

        const channelName = `conversations.${selectedConvId}`;
        const channel = echo.private(channelName);

        const handleUpdate = () => {
            qc.invalidateQueries({ queryKey: ['tutor-messages', selectedConvId] });
            qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
        };

        channel.listen('MessageCreated', handleUpdate);
        channel.listen('MessageUpdated', handleUpdate);

        return () => {
            channel.stopListening('MessageCreated');
            channel.stopListening('MessageUpdated');
        };
    }, [selectedConvId, qc]);

    // Refresh list periodically
    useEffect(() => {
        const interval = setInterval(() => {
            qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
        }, 15000);
        return () => clearInterval(interval);
    }, [qc]);

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Approve Mutation
    const approveMut = useMutation({
        mutationFn: ({ messageId, body }) => approveMessage(messageId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tutor-messages', selectedConvId] });
            qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
            // El globo del menú se refresca solo cada minuto; sin esto queda
            // contando un borrador que el usuario acaba de resolver.
            qc.invalidateQueries({ queryKey: ['tutor-pending-count'] });
        },
    });

    // Cancel Mutation
    const cancelMut = useMutation({
        mutationFn: (messageId) => cancelMessage(messageId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tutor-messages', selectedConvId] });
            qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
            // El globo del menú se refresca solo cada minuto; sin esto queda
            // contando un borrador que el usuario acaba de resolver.
            qc.invalidateQueries({ queryKey: ['tutor-pending-count'] });
        },
    });

    // Toggle Ignore (No Lead) Mutation
    const toggleIgnoreMut = useMutation({
        mutationFn: (contactId) => {
            const contact = activeConversation?.contact;
            return updateContact({ id: contactId, is_ignored: !contact?.is_ignored });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
            qc.invalidateQueries({ queryKey: ['tutor-messages', selectedConvId] });
        },
    });

    const handleApprove = () => {
        if (!pendingAiMessage) return;
        approveMut.mutate({ messageId: pendingAiMessage.id, body: editText });
    };

    const handleCancel = () => {
        if (!pendingAiMessage) return;
        cancelMut.mutate(pendingAiMessage.id);
    };

    const pendingCount = conversations.filter(c => {
        const cMsgs = Array.isArray(c.messages) ? c.messages : (c.messages?.data || []);
        return c.has_pending_tutor || c.latest_message?.is_pending_tutor_approval || cMsgs.some(m => m?.is_pending_tutor_approval);
    }).length;

    return (
        <div className="flex h-[calc(100vh-73px)] overflow-hidden bg-slate-50 dark:bg-slate-950">
            {/* Left Panel: All Conversations List */}
            <div className="w-80 border-r border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col shrink-0">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-slate-800 space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <span className="material-symbols-outlined text-violet-600 dark:text-violet-400">visibility</span>
                            Módulo Tutor AI
                        </h2>
                        {pendingCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-white animate-pulse">
                                {pendingCount} por moderar
                            </span>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-2.5 text-[18px] text-slate-400">search</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Buscar conversación..."
                            className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-xl pl-9 pr-3 py-2 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-600"
                        />
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex bg-slate-100 dark:bg-slate-800/60 p-1 rounded-xl text-xs font-semibold">
                        <button
                            onClick={() => setTabFilter('all')}
                            className={`flex-1 py-1.5 rounded-lg transition-all ${
                                tabFilter === 'all'
                                    ? 'bg-white dark:bg-slate-700 text-violet-600 dark:text-violet-300 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            Todas ({conversations.length})
                        </button>
                        <button
                            onClick={() => setTabFilter('pending')}
                            className={`flex-1 py-1.5 rounded-lg transition-all flex items-center justify-center gap-1 ${
                                tabFilter === 'pending'
                                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            Por moderar ({pendingCount})
                        </button>
                    </div>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800">
                    {loadingConvs ? (
                        <div className="p-8 text-center text-slate-400">
                            <span className="w-6 h-6 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin inline-block"></span>
                            <p className="text-xs mt-2">Cargando conversaciones...</p>
                        </div>
                    ) : filteredConversations.length === 0 ? (
                        <div className="p-8 text-center text-slate-400 space-y-1">
                            <span className="material-symbols-outlined text-[36px] text-slate-300 dark:text-slate-700">chat_bubble_outline</span>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">No hay conversaciones</p>
                            <p className="text-xs text-slate-500">
                                {tabFilter === 'pending' ? 'No hay mensajes pendientes de moderación.' : 'No se encontraron conversaciones con este filtro.'}
                            </p>
                        </div>
                    ) : (
                        filteredConversations.map(conv => {
                            const contact = conv.contact || {};
                            const isActive = conv.id === selectedConvId;
                            const convMsgs = Array.isArray(conv.messages) ? conv.messages : (conv.messages?.data || []);
                            const hasPending = conv.has_pending_tutor || conv.latest_message?.is_pending_tutor_approval || convMsgs.some(m => m?.is_pending_tutor_approval);
                            const displayName = contact.first_name ? `${contact.first_name} ${contact.last_name || ''}` : contact.name || 'Cliente';

                            return (
                                <button
                                    key={conv.id}
                                    onClick={() => setSelectedConvId(conv.id)}
                                    className={`w-full text-left p-3.5 transition-all flex items-start gap-3 border-l-4 ${
                                        isActive
                                            ? 'bg-violet-50/50 dark:bg-violet-950/20 border-violet-600 text-slate-900 dark:text-slate-100'
                                            : 'border-transparent text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                                    }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 font-bold flex items-center justify-center shrink-0">
                                        {(displayName[0] || 'C').toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <p className="font-semibold text-sm truncate text-slate-900 dark:text-slate-100">
                                                {displayName}
                                            </p>
                                            {conv.last_message_at && (
                                                <span className="text-[10px] text-slate-400 shrink-0">
                                                    {dayjs(conv.last_message_at).format('HH:mm')}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                            {conv.latest_message?.body || contact.phone || 'Sin mensajes'}
                                        </p>
                                        {hasPending && (
                                            <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/40">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                                Por moderar
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Right Panel: Conversation Feed & Header Menu */}
            <div className="flex-1 flex flex-col bg-[#efeae2] dark:bg-slate-950 overflow-hidden relative">
                {activeConversation ? (
                    <>
                        {/* Conversation Header */}
                        <div className="px-6 py-3.5 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between shrink-0 z-10 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 font-bold flex items-center justify-center">
                                    {(activeConversation.contact?.first_name?.[0] || 'C').toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100">
                                        {activeConversation.contact?.first_name ? `${activeConversation.contact.first_name} ${activeConversation.contact.last_name || ''}` : 'Cliente'}
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        {activeConversation.contact?.phone || `Canal: ${activeConversation.channel}`}
                                    </p>
                                </div>
                            </div>

                            {/* 3-Dots Dropdown Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    className={`p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition ${showMenu ? 'bg-slate-100 dark:bg-slate-800 text-violet-600' : 'text-slate-600 dark:text-slate-300'}`}
                                    title="Opciones de conversación"
                                >
                                    <span className="material-symbols-outlined text-[22px]">more_vert</span>
                                </button>

                                {showMenu && (
                                    <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-800 z-50 overflow-hidden py-1">
                                        {/* Option: Responder con IA */}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                setIsAiModalOpen(true);
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/40 transition-colors font-medium"
                                        >
                                            <span className="material-symbols-outlined text-[20px] text-violet-600 dark:text-violet-400">smart_toy</span>
                                            <span>Responder con IA</span>
                                        </button>

                                        {/* Option: Vista 360 */}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                const contactId = activeConversation?.contact?.id || activeConversation?.contact_id;
                                                if (contactId) {
                                                    navigate(`/contacts/${contactId}/360`);
                                                }
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-t border-gray-100 dark:border-slate-800"
                                        >
                                            <span className="material-symbols-outlined text-[20px] text-slate-400">360</span>
                                            <span>Vista 360</span>
                                        </button>

                                        {/* Option: Editar contacto */}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                const contactId = activeConversation?.contact?.id || activeConversation?.contact_id;
                                                if (contactId) {
                                                    navigate(`/contacts?edit=${contactId}`);
                                                }
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-t border-gray-100 dark:border-slate-800"
                                        >
                                            <span className="material-symbols-outlined text-[20px] text-slate-400">edit</span>
                                            <span>Editar contacto</span>
                                        </button>

                                        {/* Option: Ignorar / Desmarcar No Lead */}
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                const contactId = activeConversation?.contact?.id || activeConversation?.contact_id;
                                                if (contactId) {
                                                    toggleIgnoreMut.mutate(contactId);
                                                }
                                            }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-t border-gray-100 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                                        >
                                            <span className={`material-symbols-outlined text-[20px] ${activeConversation?.contact?.is_ignored ? 'text-green-500' : 'text-slate-400'}`}>
                                                {activeConversation?.contact?.is_ignored ? 'person_add' : 'person_off'}
                                            </span>
                                            <span className="font-medium">
                                                {activeConversation?.contact?.is_ignored ? 'Desmarcar como No Lead' : 'Ignorar como No Lead'}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Message Feed */}
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 no-scrollbar">
                            {loadingMessages ? (
                                <div className="h-full flex items-center justify-center text-slate-400">
                                    <span className="w-6 h-6 border-2 border-violet-600/30 border-t-violet-600 rounded-full animate-spin"></span>
                                </div>
                            ) : (
                                messages.map(msg => {
                                    const isInbound = msg.direction === 'inbound';
                                    const isPending = msg.is_pending_tutor_approval;
                                    return (
                                        <div
                                            key={msg.id}
                                            className={`flex ${isInbound ? 'justify-start' : 'justify-end'} animate-in fade-in duration-200`}
                                        >
                                            <div
                                                className={`max-w-[70%] rounded-2xl px-4 py-2.5 shadow-sm text-sm relative ${
                                                    isPending
                                                        ? 'bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-900/50 text-slate-900 dark:text-slate-100'
                                                        : isInbound
                                                        ? 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100'
                                                        : 'bg-violet-600 text-white'
                                                }`}
                                            >
                                                {/* Meta label */}
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                                                        isPending 
                                                            ? 'text-amber-800 dark:text-amber-400' 
                                                            : isInbound 
                                                            ? 'text-slate-400 dark:text-slate-500' 
                                                            : 'text-violet-200'
                                                    }`}>
                                                        {isPending ? 'Borrador IA (Moderación)' : msg.is_ai_generated ? 'IA' : isInbound ? 'Cliente' : 'Asesor'}
                                                    </span>
                                                </div>

                                                <p className="whitespace-pre-line break-words leading-relaxed">{msg.body}</p>

                                                <div className="flex items-center justify-end gap-1.5 mt-1.5 text-[10px] opacity-70">
                                                    <span>{dayjs(msg.created_at).format('HH:mm')}</span>
                                                    {!isInbound && !isPending && (
                                                        <span className="material-symbols-outlined text-[13px]">
                                                            {msg.status === 'read' ? 'done_all' : 'check'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Tutor Moderation Input Panel */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800 shrink-0 z-10">
                            {pendingAiMessage ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 px-3.5 py-2.5 rounded-xl border border-amber-200 dark:border-amber-900/50 text-xs">
                                        <span className="material-symbols-outlined text-[18px]">info</span>
                                        <div>
                                            <p className="font-bold">Respuesta sugerida por la IA retenida</p>
                                            <p className="opacity-90">Modifica el texto si es necesario y aprueba el envío, o descarta la respuesta.</p>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <textarea
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            rows={4}
                                            disabled={approveMut.isPending || cancelMut.isPending}
                                            className="w-full border border-gray-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                                            placeholder="Edita la respuesta de la IA antes de enviar..."
                                        />
                                    </div>

                                    <div className="flex items-center justify-end gap-3">
                                        <button
                                            onClick={handleCancel}
                                            disabled={approveMut.isPending || cancelMut.isPending}
                                            className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-sm transition-all"
                                        >
                                            {cancelMut.isPending ? (
                                                <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined text-[18px]">cancel</span>
                                            )}
                                            Cancelar Borrador
                                        </button>

                                        <button
                                            onClick={handleApprove}
                                            disabled={approveMut.isPending || cancelMut.isPending}
                                            className="flex items-center gap-1.5 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl text-sm shadow-md shadow-violet-600/25 transition-all"
                                        >
                                            {approveMut.isPending ? (
                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined text-[18px]">send</span>
                                            )}
                                            Aprobar y Enviar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between py-2 text-slate-500 dark:text-slate-400">
                                    <p className="text-xs">Usa el menú de 3 puntos arriba a la derecha para <strong className="text-violet-600 dark:text-violet-400">Responder con IA</strong> a esta conversación.</p>
                                    <button
                                        onClick={() => setIsAiModalOpen(true)}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-xs rounded-xl transition shadow-sm"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">smart_toy</span>
                                        Responder con IA
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 p-8">
                        <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">chat</span>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-2">Ninguna conversación seleccionada</p>
                        <p className="text-xs text-slate-500 mt-1">Selecciona una conversación del listado lateral para moderar o responder con IA.</p>
                    </div>
                )}
            </div>

            {/* AI Response Modal */}
            <AiResponseModal
                isOpen={isAiModalOpen}
                onClose={() => setIsAiModalOpen(false)}
                conversationId={selectedConvId}
                conversation={activeConversation}
                onSuccess={() => {
                    qc.invalidateQueries({ queryKey: ['tutor-messages', selectedConvId] });
                    qc.invalidateQueries({ queryKey: ['tutor-conversations'] });
                }}
            />
        </div>
    );
}
